import { Head as SeoHead } from 'components/SEO';
import { Link } from 'gatsby';
import React from 'react';
import styled from '../styled';

import { TESTS_INDEX_PATH } from '../catalog';
import { C, FONT, GUTTER, MAX_W } from '../v2/tokens';
import {
  SOFT_404_BODY,
  SOFT_404_H1,
  SOFT_404_PRIMARY_CTA,
  SOFT_404_SEARCH_LABEL,
  SOFT_404_SEARCH_PLACEHOLDER,
  SOFT_404_SECONDARY_CTA,
} from './copy';
import {
  everywhereSoft404PrimaryHref,
  everywhereSoft404SecondaryHref,
} from './inventory';

const Main = styled.main`
  max-width: ${MAX_W};
  min-height: 56vh;
  margin: 0 auto;
  padding: 72px ${GUTTER} 112px;

  h1 {
    font-family: ${FONT.display};
    font-weight: 410;
    letter-spacing: 0.01em;
    margin: 0;
    font-size: clamp(32px, 4vw, 44px);
    line-height: 1.2;
  }

  p {
    margin: 12px 0 32px;
    color: ${C.ink500};
    font-size: 16px;
  }
`;

const Ctas = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 32px;

  a {
    color: ${C.forest900};
    font-weight: 500;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;

const SearchForm = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-end;

  label {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 14px;
    font-weight: 500;
  }

  input[type='search'] {
    height: 44px;
    min-width: 240px;
    padding: 0 14px;
    border-radius: 8px;
    border: 1px solid ${C.canvas900};
    font: inherit;
  }

  button[type='submit'] {
    height: 44px;
    padding: 0 18px;
    border-radius: 9999px;
    border: 0;
    background: ${C.forest900};
    color: #fff;
    font: inherit;
    font-weight: 500;
    cursor: pointer;
  }
`;

/**
 * Shared findability-only recovery for dead Everywhere PDPs.
 * Crawlable links only; no result cards, SKUs, prices, or Order now.
 */
export default function EverywhereSoft404Recovery(): JSX.Element {
  return (
    <Main>
      <h1>{SOFT_404_H1}</h1>
      <p>{SOFT_404_BODY}</p>
      <Ctas>
        <Link to={everywhereSoft404PrimaryHref}>{SOFT_404_PRIMARY_CTA}</Link>
        <Link to={everywhereSoft404SecondaryHref}>{SOFT_404_SECONDARY_CTA}</Link>
      </Ctas>
      <SearchForm action={TESTS_INDEX_PATH} method="get">
        <label htmlFor="ew-soft404-q">
          {SOFT_404_SEARCH_LABEL}
          <input
            id="ew-soft404-q"
            type="search"
            name="q"
            placeholder={SOFT_404_SEARCH_PLACEHOLDER}
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        <button type="submit">Search</button>
      </SearchForm>
    </Main>
  );
}

export const EverywhereSoft404Head = (): JSX.Element => (
  <SeoHead
    title={`${SOFT_404_H1} | Everywhere`}
    description={SOFT_404_BODY}
    canonicalUrl="everywhere/tests"
    noIndex
  />
);
